// The assistant on a phone, after SPhoneAsk: the start (the orb, a greeting, pick up where you left off, recent
// conversations), all conversations, and the sheet that chooses what it looks at. The conversation itself, the
// waiting cards and the composer are drawn by the Assistant page, which holds the sending.
import { useNavigate } from 'react-router-dom';
import { isSuggestion, type ProposedAction } from '@dpw/shared';
import { useActions, useConversations, useMe, useSpaces } from '../lib/queries';
import { addDays, ago, arrived, localDate, localDay, localTime } from '../lib/format';
import { Orb, SkelList, type OrbState } from '../ui/motion';
import { P, PSheet, Row, Tag } from './kit';

const greeting = () => { const h = Number(localTime(new Date().toISOString()).slice(0, 2)); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; };
const whenOf = (iso: string) => arrived(iso, { today: true });

/** The cards waiting in each conversation. */
function useWaitingByConversation() {
  const w = useActions('waiting');
  const m = new Map<string, ProposedAction[]>();
  for (const a of w.data ?? []) if (a.conversationId && a.status === 'waiting' && !isSuggestion(a)) m.set(a.conversationId, [...(m.get(a.conversationId) ?? []), a]);
  return m;
}
const WAITING_WORD: Partial<Record<ProposedAction['kind'], string>> = { create_reminder: 'a reminder', create_activity: 'a task', update_activity: 'a change', send_message: 'a message', update_context: 'a context note', add_file: 'a file' };
/** "a task is waiting", "2 cards are waiting", or nothing. */
const waitingLine = (cards: ProposedAction[] | undefined) => (!cards?.length ? null
  : cards.length === 1 ? `${WAITING_WORD[cards[0]!.kind] ?? 'a card'} is waiting` : `${cards.length} cards are waiting`);

export function PhoneStart({ orb, onHistory, onVoice }: { orb: OrbState; onHistory: () => void; /** Voice mode in this chat (in the tap). */ onVoice: () => void }) {
  const me = useMe();
  const convs = useConversations();
  const nav = useNavigate();
  const waiting = useWaitingByConversation();
  const list = convs.data ?? [];
  const [last, ...rest] = list;
  return (
    <div className="ph-start">
      <div className="ph-hero2"><Orb size={96} state={orb} /><h1>{greeting()}, {me.data?.name.split(' ')[0] ?? ''}</h1><p>Ask, tell me what happened, or speak.</p><button type="button" className="ph-btf sec" onClick={onVoice}>Talk it through</button></div>
      {last && (
        <button type="button" className="ph-pick" onClick={() => nav(`/assistant/${last.id}`)}>
          <span className="pk1">Pick up where you left off</span><span className="pk2">{last.title}</span>
          <span className="pk3">{last.spaceName ? `Looking at ${last.spaceName}` : 'Looking at everything'}</span>
          <span className="pk4">{waiting.get(last.id) ? <Tag tone="a" dot>{waiting.get(last.id)!.length} {waiting.get(last.id)!.length === 1 ? 'card' : 'cards'} waiting</Tag> : null}<span className="f12 fa">{whenOf(last.updatedAt)}</span></span>
        </button>
      )}
      {rest.length > 0 && <>
        <div className="ph-asech">Recent<button type="button" onClick={onHistory}>See all</button></div>
        <div className="ph-grp flat" role="group" aria-label="Recent conversations">
          {rest.slice(0, 5).map(c => <Row key={c.id} to={`/assistant/${c.id}`} title={c.title} sub={[whenOf(c.updatedAt), waitingLine(waiting.get(c.id)) ?? c.spaceName].filter(Boolean).join(' · ')} />)}
        </div>
      </>}
      {convs.isLoading && <SkelList rows={3} variant="conv" />}
    </div>
  );
}

export function PhoneHistory({ onBack }: { onBack: () => void }) {
  const convs = useConversations();
  const waiting = useWaitingByConversation();
  const list = convs.data ?? [];
  const today = localDate();
  const weekAgo = addDays(today, -6);
  const groups: [string, typeof list][] = [
    ['Today', list.filter(c => localDay(c.updatedAt) === today)],
    ['This week', list.filter(c => localDay(c.updatedAt) !== today && localDay(c.updatedAt) >= weekAgo)],
    ['Earlier', list.filter(c => localDay(c.updatedAt) < weekAgo)],
  ];
  return (
    <>
      <div className="ph-vh"><button type="button" className="ph-back" onClick={onBack}><P.back /><span>Back</span></button></div>
      <div className="ph-lt" style={{ padding: '0 2px 4px' }}><h1 className="md">Conversations</h1></div>
      {convs.isLoading ? <SkelList rows={4} variant="conv" /> : groups.map(([h, cs]) => cs.length > 0 && (
        <div key={h}>
          <div className="ph-asech">{h}</div>
          <div className="ph-grp flat" role="group" aria-label={h}>
            {cs.map(c => <Row key={c.id} to={`/assistant/${c.id}`} title={c.title} sub={[c.spaceName, localDay(c.updatedAt) === today ? localTime(c.updatedAt) : ago(c.updatedAt)].filter(Boolean).join(' · ')}
              right={waiting.get(c.id)?.length ? <Tag tone="a">Waiting</Tag> : undefined} />)}
          </div>
        </div>
      ))}
      {!convs.isLoading && !list.length && <div className="ph-empty2">No conversations yet.</div>}
    </>
  );
}

export function ScopeSheet({ value, onPick, onClose }: { value: string | null; onPick: (spaceId: string | null) => void; onClose: () => void }) {
  const spaces = useSpaces();
  const opts: [string | null, string][] = [[null, 'Everything'], ...(spaces.data ?? []).map(s => [s.id, s.name] as [string, string])];
  return (
    <PSheet label="What should I look at?" onClose={onClose}>
      <h2>What should I look at?</h2>
      <div className="f13 mu">Answers only use this scope, and always show sources.</div>
      <div className="ph-grp flat" role="radiogroup" aria-label="Scope">
        {opts.map(([id, name]) => (
          <button key={id ?? 'all'} type="button" role="radio" aria-checked={value === id} className="ph-row" onClick={() => { onPick(id); onClose(); }}>
            <span className="ph-rt"><div className="t">{name}</div></span>{value === id && <span style={{ color: 'var(--grn-tx)', width: 20, height: 20 }}><P.check /></span>}
          </button>
        ))}
      </div>
    </PSheet>
  );
}
